import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useParams } from 'react-router-dom';

import {
  useGetBalanceMutation,
  usePostLeaveFormMutation,
} from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/leaveRequestApiSlice';
import {
  useDeleteDocumentByIdMutation,
  useFormListMutation,
  useFormUploadMutation,
} from '@Store/api/dmsApiSlice';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { FooterActions, LeaveStatusCodes, LeaveTypeCodes } from '@Enum/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { getLeaveTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { getTotalDays, parseDateTime } from '@Utils/transformDateTime';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import {
  DocumentFormUploadResponse,
  LeaveRequestFormPostRequest,
  RequestError,
} from '@Types/api/form';
import {
  transformDictToOptions,
  transformFlagsToOptions,
} from '@Utils/transformToOptions';
import { leaveRequestActions } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/leaveRequestSlice';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { NotificationContext } from '@Context/NotificationContext';
import { generateFilepath } from '@Utils/generateFilepath';
import dayjs from 'dayjs';
import { Attachment } from '@Components/types/Attachment';
import { getDocument } from '@Features/utils/document';
import { getFilepath } from '@Features/utils/getFilepath';
import { AVAILABLE_BALANCE_DATE_FORMAT } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/constants';
import { MainForm, RequiredNotification } from '@Components/index';
import { getEditFormType } from '@Features/utils/getEditFormType';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { Balance } from '@Types/api/form/leaveRequest/Balance';
import { validateForm } from '@Features/utils/validateForm';

import { LeaveRequestFormValues } from '../type';
import ReplacementDetailsSection from '../components/ReplacementDetailsSection/ReplacementDetailsSection';
import LeaveDetailsSection from '../components/LeaveDetailsSection/LeaveDetailsSection';
import { initialBalance, initialValuesForm } from '../initialValues';
import { descriptionAlert } from '../descriptionAlert';
import {
  getDocumentFolder,
  getFormUploadFileMetaData,
} from '../utils/document';
import { getLeaveRequestFormSchema } from '../validationSchema';
import useReplacementManager from '../hooks/useReplacementManager';
import { getValidationSettings } from '../../store/selectors/leaveRequestSelector';

const LeaveRequestEditForm: React.FC = () => {
  const [skipGetReplacementUsers, setSkipGetReplacementUsers] = useState(true);
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();
  const dispatch = useAppDispatch();

  const [balance, setBalance] = useState<Balance>(initialBalance);
  const [removeUploadedFile, setRemoveUploadedFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);

  const currentUser = useAppSelector(currentUserSelector);
  const leaveTypes = useAppSelector(getLeaveTypeDictSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const validationSettings = useAppSelector(getValidationSettings);
  const [cancelRequest] = useCancelHrmRequestMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [postLeaveForm] = usePostLeaveFormMutation();
  const [getBalance] = useGetBalanceMutation();
  const [formUpload] = useFormUploadMutation();
  const { data: editDataForm, refetch } = useGetHrmRequestFormQuery(
    requestId!,
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [getUser] = useGetUserInfoMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [deleteDocumentById] = useDeleteDocumentByIdMutation();
  const [getFormList] = useFormListMutation();

  const { replacementUsers, replacementEmployee, onLoadMore, onSearch } =
    useReplacementManager({
      skipGetReplacementUsers,
      replacementEmpNo: editDataForm?.replacementEmpNo,
    });

  const { handleBackToListRequests } = useNavigationToListRequests();

  const requisitionInfo = useRequisitionInfoExistingReq(editDataForm, 'Leave');

  useEffect(
    () => () => {
      dispatch(leaveRequestActions.setLeaveRequest(null));
      dispatch(fileUploadActions.setFile(null));
    },
    [requestId]
  );

  const handleUploadedFileRemove = useCallback(
    (value: boolean | undefined) => {
      setRemoveUploadedFile(Boolean(uploadedFile) && Boolean(value));
    },
    [uploadedFile]
  );

  useEffect(() => {
    setUploadedFile(undefined);
    setRemoveUploadedFile(false);
    dispatch(fileUploadActions.setFile(null));

    if (!editDataForm?.requestId || !editDataForm?.filepath) {
      return;
    }

    const documentFolderPath = getDocumentFolder(editDataForm.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, editDataForm.filepath);

        if (matchedDocument) {
          setUploadedFile({
            id: matchedDocument.documentId,
            name: editDataForm.filepath,
          });
        }
      });
  }, [editDataForm]);

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'LeaveRequest',
          (file as RcFile).name,
          editDataForm?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [editDataForm]
  );

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> | null => {
      const { formData, filepath } = currentFileUpload;

      if (!filepath) {
        return null;
      }

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmit = useCallback(
    (values: LeaveRequestFormValues) => {
      const submitFormHandler = async (): Promise<void> => {
        const filepath = getFilepath(
          currentFileUpload.filepath,
          removeUploadedFile,
          editDataForm?.filepath
        );
        let body = {
          requestId: editDataForm?.requestId,
          // reqStatusId: editDataForm?.reqStatusId,
          empNumber: editDataForm?.empNumber as string,
          startDate: values.startDate,
          endDate: values.endDate,
          remarks: values.remarks,
          telephone: values.telephone,
          replacementEmpNo: values.replacementEmpNo || '',
          totDays: Number(values.totDays),
          leaveTypeID: Number(values.leaveTypeID),
          ticketRequired: Number(values.ticketRequired),
          passportRequired: Number(values.passportRequired),
          isReplacementRequired: values.isReplacementRequired,
          filepath,
        } as LeaveRequestFormPostRequest;

        if (
          Number(values.ticketRequired) &&
          Number(values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE &&
          Number(values.ticketRequired) === 1
        ) {
          body = {
            ...body,
            totalTickets: balance.eligibleTickets ?? 0,
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
            leaveTypeID,
          } = await postLeaveForm(body).unwrap();

          dispatch(
            leaveRequestActions.setLeaveRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          if (removeUploadedFile) {
            await deleteDocumentById(uploadedFile!.id).unwrap();
          }

          if (leaveTypeID === LeaveTypeCodes.SICK_LEAVE) {
            await uploadFile(String(reqId));
          }

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitFormHandler();
    },
    [
      handleBackToListRequests,
      currentFileUpload,
      uploadFile,
      editDataForm,
      removeUploadedFile,
      uploadFile,
      uploadedFile,
    ]
  );

  const validationSchema = useMemo(() => {
    const isFileAttached =
      !!currentFileUpload.filepath || (!!uploadedFile && !removeUploadedFile);
    const { isStaff } = currentUser;
    const output = getLeaveRequestFormSchema(
      isFileAttached,
      isStaff,
      validationSettings
    );
    return output;
  }, [currentFileUpload, removeUploadedFile, uploadedFile]);

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(validationSchema, formik);

    if (!isFormValid) return;

    const filepath = getFilepath(
      currentFileUpload.filepath,
      removeUploadedFile,
      editDataForm?.filepath
    );
    let body = {
      requestId: editDataForm?.requestId,
      empNumber: editDataForm?.empNumber as string,
      startDate: formik.values.startDate,
      endDate: formik.values.endDate,
      remarks: formik.values.remarks,
      telephone: formik.values.telephone,
      replacementEmpNo: formik.values.replacementEmpNo || '',
      totDays: Number(formik.values.totDays),
      leaveTypeID: Number(formik.values.leaveTypeID),
      ticketRequired: Number(formik.values.ticketRequired),
      passportRequired: Number(formik.values.passportRequired),
      isReplacementRequired: formik.values.isReplacementRequired,
      filepath,
    } as LeaveRequestFormPostRequest;

    if (
      Number(formik.values.ticketRequired) &&
      Number(formik.values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE &&
      Number(formik.values.ticketRequired) === 1
    ) {
      body = {
        ...body,
        totalTickets: balance.eligibleTickets ?? 0,
      };
    }

    setDisableSaveForm(true);

    try {
      const {
        requestId: reqId,
        createdDate,
        reqStatusId,
        leaveTypeID,
      } = await postLeaveForm(body).unwrap();

      dispatch(
        leaveRequestActions.setLeaveRequest({
          createdDate,
          requestId: reqId,
          reqStatusId,
        })
      );

      if (removeUploadedFile) {
        await deleteDocumentById(uploadedFile!.id).unwrap();
      }

      if (leaveTypeID === LeaveTypeCodes.SICK_LEAVE) {
        await uploadFile(String(reqId));
      }

      editSuccessNotification(String(reqId));

      setDisableSaveForm(false);

      refetch();
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [
    currentFileUpload,
    editDataForm,
    uploadFile,
    removeUploadedFile,
    uploadedFile,
    formik,
    validationSchema,
  ]);

  useEffect(() => {
    // setting value to trigger validation
    const timeStamp = dayjs().toISOString();
    formik.setFieldValue('document', timeStamp);
  }, [currentFileUpload.filepath, removeUploadedFile]);

  useEffect(() => {
    if (formik.values.startDate && formik.values.endDate) {
      const totalDays = getTotalDays(
        formik.values.endDate,
        formik.values.startDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.startDate, formik.values.endDate]);

  useEffect(() => {
    if (Number(formik.values.leaveTypeID) !== LeaveTypeCodes.ANNUAL_LEAVE) {
      formik.setFieldValue('ticketRequired', 0);
    }
  }, [formik.values.leaveTypeID]);

  useEffect(() => {
    if (formik.values.startDate && editDataForm?.empNumber) {
      const startDateFormatted = parseDateTime(
        formik.values.startDate,
        AVAILABLE_BALANCE_DATE_FORMAT
      );

      getBalance({
        empNumber: editDataForm.empNumber,
        date: startDateFormatted,
      })
        .unwrap()
        .then((balanceResp) => {
          setBalance({
            asOnToday: balanceResp.asOnToday,
            leaveThisYear: balanceResp.leaveThisYear,
            ticketBalance: balanceResp.ticketBalance,
            ticketThisYear: balanceResp.ticketThisYear,
            eligibleTickets: balanceResp.eligibleTickets,
            annualLeaveThisYear: balanceResp.annualLeaveThisYear,
            emergencyLeaveThisYear: balanceResp.emergencyLeaveThisYear,
            sickLeaveThisYear: balanceResp.sickLeaveThisYear,
            asOnDate: balanceResp.asOnDate,
            isReplacementRequired: balanceResp.isReplacementRequired,
          });
          formik.setFieldValue(
            'isReplacementRequired',
            balanceResp.isReplacementRequired
          );
          setSkipGetReplacementUsers(!balanceResp.isReplacementRequired);
        })
        .catch(() => {});
    } else {
      setBalance({
        ...balance,
        asOnDate: 0,
        ticketBalance: 0,
        eligibleTickets: 0,
      });
    }
  }, [formik.values.startDate, editDataForm]);

  const leaveTypeOptions = useMemo(
    () => transformDictToOptions(leaveTypes),
    [leaveTypes]
  );
  const passportOptions = useMemo(() => transformFlagsToOptions(), []);
  const ticketOptions = useMemo(() => transformFlagsToOptions(), []);

  useEffect(() => {
    if (!editDataForm?.empNumber) {
      return;
    }

    getUser({ empNo: editDataForm.empNumber })
      .unwrap()
      .then((user) => {
        formik.setValues({
          ticketRequired: editDataForm.ticketRequired,
          passportRequired: editDataForm.passportRequired,
          startDate: editDataForm.startDate,
          endDate: editDataForm.endDate,
          telephone: editDataForm.telephone,
          leaveTypeID: String(editDataForm.leaveTypeID),
          totDays: String(editDataForm.totDays),
          empNumber: user.empNumber,
          replacementEmpNo: editDataForm.replacementEmpNo,
          replacementEmpName: replacementEmployee?.empName || '',
          replacementEmpDesignation: replacementEmployee?.occupation || '',
          remarks: editDataForm.remarks,
          isReplacementRequired: Boolean(editDataForm.isReplacementRequired),
          document: null,
        });
      });
  }, [editDataForm, replacementEmployee]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === LeaveStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        showLeaveBalance
        balance={balance}
        formType={formType}
      >
        <FormItems>
          <LeaveDetailsSection
            label="Leave Details"
            formik={formik}
            formApproval={false}
            balance={balance}
            leaveTypeOptions={leaveTypeOptions}
            passportOptions={passportOptions}
            ticketOptions={ticketOptions}
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
            uploadedFile={uploadedFile}
            onUploadedFileRemove={handleUploadedFileRemove}
          />

          <ReplacementDetailsSection
            label="Replacement Details"
            listOfUsers={replacementUsers || []}
            formik={formik}
            isReplacementRequired={balance?.isReplacementRequired}
            onSearch={onSearch}
            onLoadMore={onLoadMore}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default LeaveRequestEditForm;
